import React, { useState } from 'react';
import { AnalysisMethod, Severity } from '@/api/domains/analyses/types';
import styles from './AnalysisConfig.module.css';
import { ConfigHeader, ConfigItem, InfoDialog } from './components';
import { getMethodInfo, getStrictnessLabel } from './utils';

interface AnalysisConfigProps {
  method: AnalysisMethod;
  metadataJson: Record<string, string>;
}

export const AnalysisConfig: React.FC<AnalysisConfigProps> = ({
  method,
  metadataJson
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [infoDialog, setInfoDialog] = useState<{
    visible: boolean;
    type: string | null;
  }>({
    visible: false,
    type: null
  });

  const vectorThreshold = parseFloat(metadataJson.vector_threshold || '0.5');
  const topK = parseInt(metadataJson.top_k_candidates || '5');
  const minConfidence = parseFloat(metadataJson.min_confidence_score || '0.6');
  const minSeverity = metadataJson.min_severity as Severity;

  const methodInfo = getMethodInfo(method);
  const showInfo = (type: string) => setInfoDialog({ visible: true, type });

  return (
    <>
      <InfoDialog
        visible={infoDialog.visible}
        type={infoDialog.type}
        method={method}
        onHide={() => setInfoDialog({ visible: false, type: null })}
      />

      <div className={styles.configContainer}>
        <ConfigHeader
          methodIcon={methodInfo.icon}
          methodTitle={methodInfo.title}
          minConfidence={minConfidence}
          isExpanded={isExpanded}
          onToggle={() => setIsExpanded(!isExpanded)}
        />

        {isExpanded && (
          <div className={styles.configDetails}>
            <div className={styles.configGrid}>
              <ConfigItem
                label="Analysis Method"
                onInfoClick={() => showInfo('method')}
                value={
                  <>
                    <i className={`pi ${methodInfo.icon}`} />
                    <span>{methodInfo.title}</span>
                  </>
                }
              />

              <ConfigItem
                label="Match Strictness"
                onInfoClick={() => showInfo('strictness')}
                value={
                  <>
                    <span className={styles.badge}>
                      {getStrictnessLabel(vectorThreshold)}
                    </span>
                    <span className={styles.detail}>
                      {Math.round(vectorThreshold * 100)}%
                    </span>
                  </>
                }
              />

              <ConfigItem
                label="Search Depth"
                onInfoClick={() => showInfo('depth')}
                value={
                  <>
                    <span className={styles.badge}>{topK}</span>
                    <span className={styles.detail}>
                      ~{10 * topK} overlaps/regulation
                    </span>
                  </>
                }
              />

              <ConfigItem
                label="AI Confidence"
                onInfoClick={() => showInfo('confidence')}
                value={
                  <>
                    <span className={styles.badge}>
                      {Math.round(minConfidence * 100)}%
                    </span>
                    <span className={styles.detail}>minimum threshold</span>
                  </>
                }
              />

              <ConfigItem
                label="Min. Severity"
                value={
                  <span
                    className={`${styles.severityBadge} ${styles[minSeverity]}`}
                  >
                    {minSeverity}
                  </span>
                }
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};
